import { cs } from '../../lib/currency';
/**
 * UniformOrdersTable — admin Uniform Orders table with:
 *   • Sortable columns (via <SortableTable>)
 *   • Pagination (25/page, configurable)
 *   • Column-picker (toggle visibility of optional columns)
 *   • Per-user persistence (via useTablePreferences hook)
 *
 * iter307an (Feb 2026).
 *
 * Mandatory columns: parent, for, items, status, actions.
 * Optional columns:  parent_email, total_amount, order_date,
 *                    invoice_id, created_by_admin.
 */
import React, { useMemo, useState } from 'react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Settings2, Eye, Edit, Trash2 } from 'lucide-react';
import SortableTable from '../ui/sortableTable/SortableTable';
import useTablePreferences from '../../hooks/useTablePreferences';

// Definition of every available column, keyed for visibility lookups.
const COL_DEFS = {
  parent:           { label: 'Parent',        required: true,  sortable: true, type: 'string' },
  parent_email:     { label: 'Email',         required: false, sortable: true, type: 'string' },
  for_who:          { label: 'For',           required: true,  sortable: true, type: 'string' },
  items:            { label: 'Items',         required: true,  sortable: false },
  total_amount:     { label: 'Total',         required: false, sortable: true, type: 'numeric', align: 'right' },
  status:           { label: 'Status',        required: true,  sortable: true, type: 'string' },
  order_date:       { label: 'Order date',    required: false, sortable: true, type: 'date' },
  invoice_id:       { label: 'Invoice',       required: false, sortable: true, type: 'string' },
  created_by_admin: { label: 'Created by',    required: false, sortable: true, type: 'string' },
  actions:          { label: '',              required: true,  sortable: false, align: 'right' },
};

// Defaults: required cols + a couple of useful extras toggled on.
const DEFAULT_VISIBLE = {
  parent: true, parent_email: false, for_who: true, items: true,
  total_amount: true, status: true, order_date: true,
  invoice_id: false, created_by_admin: false, actions: true,
};

const STATUS_TONE = {
  processed: 'bg-green-100 text-green-700',
  confirmed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
  pending:   'bg-amber-100 text-amber-700',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(iso).slice(0, 10); }
};

const fmtMoney = (n) => (n == null ? '' : `${cs()}${Number(n).toFixed(2)}`);

export default function UniformOrdersTable({
  orders, getItemLabel,
  onView, onEdit, onDelete, onMarkProcessed,
}) {
  const {
    visibleColumns, setVisibleColumns, resetPreferences, loaded,
  } = useTablePreferences('admin_uniform_orders', DEFAULT_VISIBLE);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Build the columns array dynamically from visibility + COL_DEFS.
  const columns = useMemo(() => {
    if (!loaded) return [];
    const out = [];
    const isVisible = (k) => visibleColumns[k] ?? DEFAULT_VISIBLE[k] ?? false;

    if (isVisible('parent')) {
      out.push({
        key: 'parent', label: COL_DEFS.parent.label, type: 'string', sortable: true,
        accessor: (r) => r.parent_name || r.parent_email || '',
        render: (r) => (
          <div>
            <p className="font-medium">{r.parent_name || '—'}</p>
            {!isVisible('parent_email') && <p className="text-xs text-slate-500">{r.parent_email}</p>}
          </div>
        ),
      });
    }
    if (isVisible('parent_email')) {
      out.push({
        key: 'parent_email', label: COL_DEFS.parent_email.label, type: 'string', sortable: true,
        accessor: (r) => r.parent_email || '',
        render: (r) => <span className="text-xs text-slate-700">{r.parent_email || '—'}</span>,
      });
    }
    if (isVisible('for_who')) {
      out.push({
        key: 'for_who', label: COL_DEFS.for_who.label, type: 'string', sortable: true,
        accessor: (r) => r.is_for_parent ? 'Self' : (r.player_data?.full_name || ''),
        render: (r) => r.is_for_parent
          ? <Badge variant="outline">Self</Badge>
          : <span>{r.player_data?.full_name || 'Player'}</span>,
      });
    }
    if (isVisible('items')) {
      out.push({
        key: 'items', label: COL_DEFS.items.label, sortable: false,
        render: (r) => (r.items?.length > 0 ? (
          <div className="text-sm">
            {r.items.map((it, i) => (
              <span key={i} className="mr-2">{it.quantity}× {getItemLabel(it.item_type)}</span>
            ))}
          </div>
        ) : <span className="text-slate-400">Verification only</span>),
      });
    }
    if (isVisible('total_amount')) {
      out.push({
        key: 'total_amount', label: COL_DEFS.total_amount.label, type: 'numeric',
        sortable: true, align: 'right',
        accessor: (r) => Number(r.total_amount || 0),
        render: (r) => r.total_amount != null
          ? <span className="font-medium">{fmtMoney(r.total_amount)}</span>
          : <span className="text-slate-300">—</span>,
      });
    }
    if (isVisible('status')) {
      out.push({
        key: 'status', label: COL_DEFS.status.label, type: 'string', sortable: true,
        accessor: (r) => r.status || '',
        render: (r) => (
          <Badge className={STATUS_TONE[r.status] || STATUS_TONE.pending}>
            {r.status || 'pending'}
          </Badge>
        ),
      });
    }
    if (isVisible('order_date')) {
      out.push({
        key: 'order_date', label: COL_DEFS.order_date.label, type: 'date', sortable: true,
        accessor: (r) => r.created_at,
        render: (r) => <span className="text-sm text-slate-500">{fmtDate(r.created_at)}</span>,
      });
    }
    if (isVisible('invoice_id')) {
      out.push({
        key: 'invoice_id', label: COL_DEFS.invoice_id.label, type: 'string', sortable: true,
        accessor: (r) => r.manual_invoice_id || '',
        render: (r) => r.manual_invoice_id
          ? <span className="text-xs font-mono text-slate-600">{r.manual_invoice_id.slice(0, 8)}…</span>
          : <span className="text-slate-300 text-xs">—</span>,
      });
    }
    if (isVisible('created_by_admin')) {
      out.push({
        key: 'created_by_admin', label: COL_DEFS.created_by_admin.label, type: 'string', sortable: true,
        accessor: (r) => r.created_by_admin ? 'Admin' : 'Parent',
        render: (r) => r.created_by_admin
          ? <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">Admin</Badge>
          : <Badge variant="outline" className="text-xs">Parent</Badge>,
      });
    }
    // Actions always last.
    out.push({
      key: 'actions', label: '', sortable: false, align: 'right',
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => onView?.(r)} title="View details" data-testid={`uniform-order-view-${r.id}`}>
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit?.(r)} title="Edit" data-testid={`uniform-order-edit-${r.id}`}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete?.(r)} className="text-red-500 hover:text-red-700" title="Delete" data-testid={`uniform-order-delete-${r.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
          {r.status === 'pending' && (
            <Button
              variant="outline" size="sm"
              onClick={() => onMarkProcessed?.(r)}
              title="Confirms the order is done (uniform delivered/ready) and auto-generates an unpaid Uniform invoice for the parent."
              data-testid={`uniform-order-mark-processed-${r.id}`}
            >
              Mark Processed
            </Button>
          )}
        </div>
      ),
    });

    return out;
  }, [visibleColumns, loaded, getItemLabel, onView, onEdit, onDelete, onMarkProcessed]);

  const toggleCol = (key) => {
    if (COL_DEFS[key]?.required) return;
    setVisibleColumns((prev) => ({ ...prev, [key]: !(prev[key] ?? DEFAULT_VISIBLE[key]) }));
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="uniform-orders-column-picker">
              <Settings2 className="h-4 w-4 mr-2" />
              Columns
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56">
            <p className="text-xs font-semibold text-slate-700 mb-2">Visible columns</p>
            <div className="space-y-1.5">
              {Object.entries(COL_DEFS)
                .filter(([k]) => !['actions', 'items', 'status', 'for_who', 'parent'].includes(k))
                .map(([k, def]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={!!(visibleColumns[k] ?? DEFAULT_VISIBLE[k])}
                      onCheckedChange={() => toggleCol(k)}
                      disabled={def.required}
                      data-testid={`uniform-orders-col-toggle-${k}`}
                    />
                    <span className={def.required ? 'text-slate-400' : ''}>{def.label}</span>
                  </label>
                ))}
            </div>
            <div className="border-t border-slate-200 mt-3 pt-2">
              <Button variant="ghost" size="sm" onClick={resetPreferences} className="w-full text-xs" data-testid="uniform-orders-cols-reset">
                Reset to defaults
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <SortableTable
        data={orders}
        columns={columns}
        defaultSort={{ field: 'order_date', direction: 'desc' }}
        pageSize={25}
        getRowKey={(r) => r.id}
        testidPrefix="uniform-orders"
        emptyMessage="No orders submitted for this campaign yet."
      />
    </div>
  );
}
